import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, FileText, Users } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { formatBytes } from "../../lib/portalFiles";
import { fromDeadlineParts, toDeadlineParts } from "../../lib/portalKinds";
import { libraryStatus } from "../../lib/courseLessons";
import DeadlineField from "./DeadlineField";
import { downloadFile } from "./SubmissionHistory";
import { Chip, fieldCls, primaryBtn, secondaryBtn } from "./PortalShell";
import ChangeRequest from "./ChangeRequest";

/**
 * The trainer's page for an activity from the course library (28.10.2026):
 * its content is the library's, so no editor — only when it appears, whether
 * it shows, its deadline and a note of their own (decisions 3 and 12). Every
 * change is noted, and stays when the group's sessions move.
 */
export default function LibraryControls({ data, reload }) {
  const a = data.activity;
  const task = a.kind === "assignment";
  const notes = a.library_kind === "trainer_notes";
  const [shows, setShows] = useState(toDeadlineParts(a.visible_from));
  const [due, setDue] = useState(toDeadlineParts(a.due_at));
  const [note, setNote] = useState(a.trainer_note || "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const status = libraryStatus(a);

  const patch = async (body, ok) => {
    setBusy(true);
    try {
      await api.patch(`/portal/activities/${a.id}`, body);
      setMessage({ tone: "good", text: ok });
      await reload();
    } catch (err) {
      setMessage({ tone: "bad", text: apiErrorMessage(err, "Nu am putut salva.") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-8 grid gap-4" data-testid="portal-library-controls">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-xl font-bold text-[#373A45]">Pentru formator</h2>
        <Chip tone={status.tone}>{status.label}</Chip>
        {task && <span className="text-sm text-[#6B7280]">{data.submitted_count === 1 ? "o predare" : `${data.submitted_count || 0} predări`}</span>}
      </div>
      <p className="text-sm text-[#6B7280]">
        Conținutul vine din biblioteca cursului și îl schimbă echipa FPED. Aici alegi când îl văd cursanții, termenul și
        o notă a ta. Ce schimbi rămâne așa și dacă se mută sesiunile.{task ? " Pentru altă cerință, cere o modificare." : ""}
      </p>

      {(a.trainer_files || []).length > 0 && (
        <div className="grid gap-2 rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid="portal-trainer-files">
          <p className="text-sm font-semibold text-[#373A45]">Doar pentru formatori</p>
          <FileList files={a.trainer_files} />
        </div>
      )}
      {(a.files || []).length > 0 && (
        <div className="grid gap-2 rounded-2xl border border-[#E5E7EB] bg-white p-5">
          <p className="text-sm font-semibold text-[#373A45]">{notes ? "Fișierele" : "Ce văd și cursanții"}</p>
          <FileList files={a.files} />
        </div>
      )}

      {task && <ChangeRequest activity={a} />}

      {task && (
        <Link to={`/portal/grupa/${a.group_id}`}
          className="flex items-center gap-3 rounded-xl bg-[#965EA7]/[0.07] px-4 py-3 text-sm font-semibold text-[#7A4986] hover:bg-[#965EA7]/[0.12]">
          <Users size={16} /> Predările cursanților și feedbackul
          <ChevronRight size={16} className="ml-auto" />
        </Link>
      )}

      {!notes && (
        <div className="grid gap-3 rounded-2xl border border-[#E5E7EB] bg-white p-5">
          <div className="grid gap-1 text-sm font-semibold text-[#373A45]">
            Când îl văd cursanții
            <DeadlineField value={shows} onChange={setShows} testid="portal-library-shows" />
            <span className="text-xs font-normal text-[#6B7280]">Ora României. Implicit, la începutul sesiunii lecției.</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} className={secondaryBtn}
              onClick={() => patch({ visible_from: new Date().toISOString(), published: true }, "Cursanții îl văd de acum.")}
              data-testid="portal-library-now">
              Publică acum
            </button>
            <button type="button" disabled={busy || !shows.date} className={secondaryBtn}
              onClick={() => patch({ visible_from: fromDeadlineParts(shows.date, shows.time) }, "Am mutat data.")}>
              Salvează data
            </button>
            <button type="button" disabled={busy} className={secondaryBtn}
              onClick={() => patch({ published: !a.published }, a.published ? "Am ascuns de la cursanți." : "Cursanții îl văd din nou.")}
              data-testid="portal-library-toggle">
              {a.published ? "Ascunde de la cursanți" : "Arată la cursanți"}
            </button>
          </div>
          {task && (
            <div className="grid gap-1 text-sm font-semibold text-[#373A45]">
              Termen
              <DeadlineField value={due} onChange={setDue} testid="portal-library-due" />
              <div>
                <button type="button" disabled={busy || !due.date} className={secondaryBtn}
                  onClick={() => patch({ due_at: fromDeadlineParts(due.date, due.time) }, "Am mutat termenul.")}>
                  Salvează termenul
                </button>
              </div>
            </div>
          )}
          <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
            Nota ta {task ? "(apare sub cerință)" : "(apare sub material)"}
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} className={fieldCls}
              placeholder="De exemplu: Lucrați pe disciplina și clasa alese la lecția 1." data-testid="portal-library-note" />
          </label>
          <div>
            <button type="button" disabled={busy || note === (a.trainer_note || "")} className={primaryBtn}
              onClick={() => patch({ trainer_note: note }, "Am salvat nota.")}>
              Salvează nota
            </button>
          </div>
          {message && (
            <p className={`text-sm ${message.tone === "bad" ? "text-[#c0483f]" : "text-[#3c7457]"}`} role="status">{message.text}</p>
          )}
        </div>
      )}
    </section>
  );
}

function FileList({ files }) {
  const [error, setError] = useState("");
  return (
    <>
      {error && <p className="text-sm text-[#c0483f]" role="alert">{error}</p>}
      <ul className="grid gap-2">
        {files.map((f) => (
          <li key={f.id}>
            <button type="button" onClick={() => downloadFile(f.id, setError)}
              className="inline-flex items-center gap-2 text-left text-sm font-semibold text-[#373A45] hover:text-[#965EA7]">
              <FileText size={15} className="shrink-0" /> {f.name}
              <span className="text-xs font-normal text-[#6B7280]">{formatBytes(f.size)}</span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
